import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Table,
  Input,
  Button,
  Tag,
  Typography,
  DatePicker,
  message,
} from "antd";
import moment from "moment";
import {
  FaRegCalendarAlt,
  FaUser,
  FaRegStickyNote,
  FaFileExcel,
} from "react-icons/fa";
import { Panel, PanelBody, PanelHeader } from "../../util/panel";
import SelectOption from "../pos/components/SelectOption";
import { axios, getToken, URL_API } from "../../../utils";
import { resCatch } from "../../util/helper";

const { Text } = Typography;
const { Search } = Input;
const { RangePicker } = DatePicker;

const getHeaders = () => ({ Authorization: "Bearer " + getToken() });

// :: API Helper ::
const getActivityLogsApi = async (limit = 200) => {
  const response = await axios({
    url: `${URL_API}/crm/conversation-note`,
    headers: getHeaders(),
    method: "get",
    params: { limit },
  });
  return response.data?.data;
};

// :: Constants ::
const appointmentFilterOptions = [
  { text: "มีการนัดหมาย", value: "has_appt" },
  { text: "ไม่มีการนัดหมาย", value: "no_appt" },
];

const contactResultFilterOptions = [
  { text: "1) สั่งซื้อ", value: "สั่งซื้อ" },
  { text: "2) สั่งจอง", value: "สั่งจอง" },
  { text: "3) ลูกค้าให้นัดติดต่อกลับ/ยังไม่สะดวกคุย", value: "ลูกค้าให้นัดติดต่อกลับ/ยังไม่สะดวกคุย" },
  { text: "4) ไม่สนใจ_(ระบุสาเหตุ)", value: "ไม่สนใจ_(ระบุสาเหตุ)" },
  { text: "5) ไม่รับสาย/ปิดเครื่อง/ติดต่อไม่ได้", value: "ไม่รับสาย/ปิดเครื่อง/ติดต่อไม่ได้" },
  { text: "6) ไม่รับสายครบ 3 ครั้ง", value: "ไม่รับสายครบ 3 ครั้ง" },
  { text: "7) Complain case", value: "Complain case" },
  { text: "8) Lead ซ้ำ", value: "Lead ซ้ำ" },
  { text: "9) Backlist", value: "Backlist" },
  { text: "10) เบอร์ผิด/เสีย/สลับ", value: "เบอร์ผิด/เสีย/สลับ" },
  { text: "11) ไม่สนใจ_ห้ามติดต่ออีก(ระบุสาเหตุ)", value: "ไม่สนใจ_ห้ามติดต่ออีก(ระบุสาเหตุ)" },
  { text: "12) สั่งซื้อช่องทางอื่นแล้ว", value: "สั่งซื้อช่องทางอื่นแล้ว" },
  { text: "13) ติดต่อไม่ได้ รอการ Follow Up อีกครั้ง", value: "ติดต่อไม่ได้ รอการ Follow Up อีกครั้ง" },
  { text: "14) Follow Up_Unsuccess_ติดต่อไม่ได้", value: "Follow Up_Unsuccess_ติดต่อไม่ได้" },
  { text: "15) สอบถามทั่วไป", value: "สอบถามทั่วไป" },
];

// :: Tag color/style config map (replaces if-else chain) ::
const TAG_BASE_STYLE = { borderRadius: "10px", padding: "0 8px", fontSize: "11px", fontWeight: "bold" };

const RESULT_TAG_CONFIG = {
  "สั่งซื้อ": { color: "green" },
  "สั่งจอง": { color: "cyan" },
  "ลูกค้าให้นัดติดต่อกลับ/ยังไม่สะดวกคุย": { color: "gold" },
  "ไม่สนใจ_(ระบุสาเหตุ)": { color: "volcano" },
  "ไม่รับสาย/ปิดเครื่อง/ติดต่อไม่ได้": { style: { color: "#262626", background: "#f0f0f0", borderColor: "#d9d9d9" } },
  "ไม่รับสาย": { style: { color: "#262626", background: "#f0f0f0", borderColor: "#d9d9d9" } },
  "ไม่รับสายครบ 3 ครั้ง": { color: "red" },
  "Complain case": { color: "purple" },
  "Lead ซ้ำ": { color: "blue" },
  "Backlist": { style: { color: "#fff", background: "#262626", borderColor: "#262626" } },
  "เบอร์ผิด/เสีย/สลับ": { color: "orange" },
  "ไม่สนใจ_ห้ามติดต่ออีก(ระบุสาเหตุ)": { color: "magenta" },
  "สั่งซื้อช่องทางอื่นแล้ว": { color: "geekblue" },
  "ติดต่อไม่ได้ รอการ Follow Up อีกครั้ง": { color: "gold" },
  "Follow Up_Unsuccess_ติดต่อไม่ได้": { style: { color: "#595959", background: "#e8e8e8" } },
  "สอบถามทั่วไป": { color: "blue" },
};

const renderResultTag = (text, reason) => {
  if (!text || text === "-") return <span style={{ color: "#bfbfbf" }}>-</span>;

  const config = RESULT_TAG_CONFIG[text] || {};
  const tag = (
    <Tag
      color={config.color}
      style={{ ...TAG_BASE_STYLE, ...(config.style || {}) }}
    >
      {text}
    </Tag>
  );

  return (
    <div>
      {tag}
      {reason && (
        <div style={{ fontSize: "11px", color: "#4b5563", marginTop: "3px" }}>
          สาเหตุ: <span style={{ color: "#1f2937", fontWeight: "600" }}>{reason}</span>
        </div>
      )}
    </div>
  );
};

// :: Check if text exceeds line threshold ::
const checkIfTextExceedsLines = (text, maxLines = 3, charsPerLine = 35) => {
  if (!text) return false;
  const lines = text.split("\n");
  let lineCount = 0;
  for (const line of lines) {
    lineCount += Math.max(1, Math.ceil(line.length / charsPerLine));
  }
  return lineCount > maxLines;
};

// :: Expandable Text Component (Max 3 lines + อ่านเพิ่มเติม / ย่อขนาด) ::
const ExpandableText = ({ text, maxLines = 3, charsPerLine = 35, textStyle }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!text || text === "-") return <span style={{ color: "#bfbfbf" }}>-</span>;

  const showToggle = checkIfTextExceedsLines(text, maxLines, charsPerLine);

  return (
    <div>
      <div
        style={{
          wordBreak: "break-word",
          overflowWrap: "anywhere",
          whiteSpace: "pre-wrap",
          lineHeight: "1.45",
          ...textStyle,
          ...(!isExpanded
            ? {
                display: "-webkit-box",
                WebkitLineClamp: maxLines,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }
            : {}),
        }}
      >
        {text}
      </div>

      {showToggle && (
        <span
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          style={{
            color: "#0d9488",
            fontSize: "11px",
            fontWeight: "700",
            cursor: "pointer",
            marginTop: "3px",
            display: "inline-block",
            userSelect: "none",
          }}
        >
          {isExpanded ? "ย่อขนาด" : "อ่านเพิ่มเติม"}
        </span>
      )}
    </div>
  );
};

// :: Export CSV helper ::
const exportToCsv = (data) => {
  const headers = [
    "ลำดับ", "วันที่ติดต่อ", "รหัสสมาชิก", "หัวข้อ", "เนื้อหา",
    "ผลการติดต่อ", "สาเหตุ", "การนัดหมายครั้งถัดไป", "รายละเอียดการนัดหมาย", "ผู้บันทึก",
  ];

  const rows = data.map((item, index) => [
    index + 1,
    `"${item.created_at || ""}"`,
    `"${item.member_code || ""}"`,
    `"${(item.note_title || "").replace(/"/g, '""')}"`,
    `"${(item.note_content || "").replace(/"/g, '""')}"`,
    `"${item.result || ""}"`,
    `"${item.reason || ""}"`,
    `"${item.has_next_appointment && item.next_appointment_date ? item.next_appointment_date : "-"}"`,
    `"${(item.next_appointment_note || "").replace(/"/g, '""')}"`,
    `"${(item.created_by || "").replace(/"/g, '""')}"`,
  ]);

  const csvContent = "\uFEFF" + [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", `Activity_Log_${moment().format("YYYYMMDD_HHmmss")}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// :: Component ::
const ActivityLog = () => {
  const [query, setQuery] = useState("");
  const [apptFilter, setApptFilter] = useState("");
  const [resultFilter, setResultFilter] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [apiLogs, setApiLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const loadActivityLogs = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getActivityLogsApi(200);
      if (data?.record) setApiLogs(data.record);
    } catch (error) {
      resCatch(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadActivityLogs();
  }, [loadActivityLogs]);

  const mappedLogs = useMemo(
    () =>
      apiLogs.map((item, index) => {
        const createMoment = item.create_date ? moment(item.create_date) : null;
        const apptMoment = item.appointment_date ? moment(item.appointment_date) : null;
        return {
          id: item.id || index,
          created_at: createMoment?.isValid() ? createMoment.format("YYYY-MM-DD HH:mm") : "-",
          member_code: item.partner_code || "-",
          note_title: item.title || "-",
          note_content: item.description || "-",
          result: item.contact_result || "-",
          reason: item.contact_reason || null,
          has_next_appointment: Boolean(item.appointment_date || item.is_appointment),
          next_appointment_date: apptMoment?.isValid() ? apptMoment.format("YYYY-MM-DD HH:mm") : null,
          next_appointment_note: item.appointment_note || "",
          created_by: item.create_by || "-",
        };
      }),
    [apiLogs]
  );

  const filteredData = useMemo(() => {
    let result = [...mappedLogs];

    if (query.trim()) {
      const q = query.trim().toLowerCase();
      result = result.filter(
        (item) =>
          item.member_code.toLowerCase().includes(q) ||
          item.note_title.toLowerCase().includes(q) ||
          item.note_content.toLowerCase().includes(q) ||
          item.result?.toLowerCase().includes(q) ||
          item.reason?.toLowerCase().includes(q) ||
          item.created_by.toLowerCase().includes(q)
      );
    }

    if (resultFilter) result = result.filter((item) => item.result === resultFilter);

    if (apptFilter === "has_appt") result = result.filter((item) => item.has_next_appointment);
    else if (apptFilter === "no_appt") result = result.filter((item) => !item.has_next_appointment);

    if (dateRange?.length === 2) {
      const start = dateRange[0].startOf("day");
      const end = dateRange[1].endOf("day");
      result = result.filter((item) =>
        moment(item.created_at, "YYYY-MM-DD HH:mm").isBetween(start, end, null, "[]")
      );
    }

    return result.sort(
      (a, b) =>
        moment(b.created_at, "YYYY-MM-DD HH:mm").valueOf() -
        moment(a.created_at, "YYYY-MM-DD HH:mm").valueOf()
    );
  }, [mappedLogs, query, apptFilter, resultFilter, dateRange]);

  const handleResetFilters = () => {
    setQuery("");
    setApptFilter("");
    setResultFilter("");
    setDateRange(null);
    setPage(1);
  };

  const handleExportExcel = () => {
    if (filteredData.length === 0) {
      message.warning("ไม่มีข้อมูลสำหรับส่งออก");
      return;
    }
    exportToCsv(filteredData);
    message.success("ส่งออกไฟล์ Excel (CSV) ตาม Filter ที่กรอกเรียบร้อยแล้ว");
  };

  const filterLabelStyle = { fontSize: "11px", color: "#4b5563", fontWeight: "600", marginBottom: "4px" };
  const filterSubStyle = { fontSize: "10px", color: "#9ca3af", fontWeight: "normal" };

  return (
    <Panel>
      <PanelHeader>
        <div style={{ display: "inline-flex", alignItems: "center" }}>
          <FaRegStickyNote style={{ marginRight: "8px" }} />
          <span>กิจกรรมประวัติการบันทึก (Activity Log)</span>
        </div>
      </PanelHeader>
      <PanelBody>
        <Row gutter={[12, 12]} type="flex" align="bottom" className="mb-4">
          <Col span={5}>
            <div style={filterLabelStyle}>
              ค้นหา <span style={filterSubStyle}>Keyword</span>
            </div>
            <Search
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="รหัสสมาชิก / หัวข้อ / เนื้อหา..."
              allowClear
              onSearch={(value) => { setQuery(value); setPage(1); }}
              style={{ width: "100%" }}
            />
          </Col>

          <Col span={5}>
            <div style={filterLabelStyle}>
              ผลการติดต่อ <span style={filterSubStyle}>Result</span>
            </div>
            <SelectOption
              withAll
              style={{ width: "100%" }}
              value={resultFilter}
              options={contactResultFilterOptions}
              onChange={(e) => { setResultFilter(e); setPage(1); }}
            />
          </Col>

          <Col span={4}>
            <div style={filterLabelStyle}>
              สถานะนัดหมาย <span style={filterSubStyle}>Appointment</span>
            </div>
            <SelectOption
              withAll
              style={{ width: "100%" }}
              value={apptFilter}
              options={appointmentFilterOptions}
              onChange={(e) => { setApptFilter(e); setPage(1); }}
            />
          </Col>

          <Col span={5}>
            <div style={filterLabelStyle}>
              ช่วงวันที่ติดต่อ <span style={filterSubStyle}>Date Range</span>
            </div>
            <RangePicker
              format="YYYY-MM-DD"
              value={dateRange}
              onChange={(dates) => { setDateRange(dates); setPage(1); }}
              style={{ width: "100%" }}
            />
          </Col>

          <Col span={5} className="d-flex justify-content-end align-items-center" style={{ gap: "8px" }}>
            <Button
              onClick={handleResetFilters}
              icon="reload"
              style={{ display: "inline-flex", alignItems: "center" }}
            >
              ล้างตัวกรอง
            </Button>
            <Button
              type="primary"
              onClick={handleExportExcel}
              style={{ display: "inline-flex", alignItems: "center" }}
            >
              <FaFileExcel style={{ marginRight: "6px" }} />
              Export Excel
            </Button>
          </Col>
        </Row>

        <style>{`
          .activity-log-table .ant-table-tbody > tr > td {
            vertical-align: top !important;
          }
        `}</style>

        <Table
          className="activity-log-table"
          bordered
          size="medium"
          dataSource={filteredData}
          loading={isLoading}
          rowKey="id"
          scroll={{ x: "max-content" }}
          pagination={{
            current: page,
            pageSize,
            total: filteredData.length,
            showLessItems: false,
            size: "default",
            onChange: (currPage) => setPage(currPage),
          }}
        >
          <Table.Column
            title="ลำดับ"
            dataIndex="id"
            width={65}
            align="left"
            render={(_, __, index) => (page - 1) * pageSize + index + 1}
          />
          <Table.Column
            title="วันที่ติดต่อ"
            dataIndex="created_at"
            width={140}
            align="left"
            render={(text) => <span style={{ fontSize: "13px", color: "#595959" }}>{text}</span>}
          />
          <Table.Column
            title="รหัสสมาชิก"
            dataIndex="member_code"
            width={120}
            align="left"
            render={(text) => <Tag color="blue" style={{ fontSize: "12px", fontWeight: "bold" }}>{text}</Tag>}
          />
          <Table.Column
            title="ผลการติดต่อ"
            dataIndex="result"
            width={170}
            align="left"
            render={(text, record) => renderResultTag(text, record.reason)}
          />
          <Table.Column
            title="รายละเอียด"
            dataIndex="note_content"
            width={280}
            align="left"
            render={(text) => <ExpandableText text={text} maxLines={3} charsPerLine={32} textStyle={{ color: "#434343" }} />}
          />
          <Table.Column
            title="การนัดหมายครั้งถัดไป"
            dataIndex="next_appointment_date"
            width={170}
            align="left"
            render={(text, record) =>
              record.has_next_appointment && text ? (
                <Tag color="orange" style={{ fontSize: "12px", padding: "2px 8px" }}>
                  <FaRegCalendarAlt style={{ marginRight: "4px" }} />
                  {text}
                </Tag>
              ) : (
                <span style={{ color: "#bfbfbf" }}>-</span>
              )
            }
          />
          <Table.Column
            title="รายละเอียดการนัดหมาย"
            dataIndex="next_appointment_note"
            width={200}
            align="left"
            render={(text, record) =>
              record.has_next_appointment && text ? (
                <ExpandableText text={text} maxLines={3} charsPerLine={24} textStyle={{ color: "#d46b08", fontSize: "13px" }} />
              ) : (
                <span style={{ color: "#bfbfbf" }}>-</span>
              )
            }
          />
          <Table.Column
            title="ผู้บันทึก"
            dataIndex="created_by"
            width={160}
            align="left"
            render={(text) => (
              <span style={{ display: "inline-flex", alignItems: "center", color: "#262626", whiteSpace: "nowrap" }}>
                <FaUser style={{ marginRight: "6px", color: "#8c8c8c", fontSize: "12px", flexShrink: 0 }} />
                <span>{text}</span>
              </span>
            )}
          />
        </Table>
      </PanelBody>
    </Panel>
  );
};

export default ActivityLog;
